import { Auth0Provider } from "@auth0/auth0-react";
import { ReactNode } from "react";
import { clearReturnTo } from "./authRecoveryRef";

function requireEnv(name: string): string {
  const value = import.meta.env[name as keyof ImportMetaEnv];
  if (typeof value !== "string" || value.length === 0) {
    throw new Error(
      `SP4c: ${name} is not set. Add it to .env.local (dev) or Vercel project settings (prod). ` +
      `See docs/superpowers/specs/2026-05-17-sp4c-threads-fe-auth0-design.md §4.`
    );
  }
  return value;
}

interface Auth0BootstrapProps {
  children: ReactNode;
}

function onRedirectCallback(appState?: { returnTo?: string }) {
  // The round-trip has landed, so the remembered destination has served its
  // purpose — drop it before it can misdirect a later, unrelated login.
  clearReturnTo();
  const target = appState?.returnTo || "/";
  window.history.replaceState({}, document.title, target);
  window.dispatchEvent(new PopStateEvent("popstate"));
}

/**
 * SP4c — Wraps the app root in <Auth0Provider> configured for the Threads
 * Auth0 application. Validates the three VITE_PUBLIC_AUTH0_* env vars at
 * boot so misconfiguration fails fast and loud.
 *
 * Mounted by main.tsx, ABOVE the existing <AuthProvider> so useAuth0() is
 * available everywhere downstream including inside Auth0CookieSync.
 */
export function Auth0Bootstrap({ children }: Auth0BootstrapProps) {
  const domain = requireEnv("VITE_PUBLIC_AUTH0_DOMAIN");
  const clientId = requireEnv("VITE_PUBLIC_AUTH0_CLIENT_ID");
  const audience = requireEnv("VITE_PUBLIC_AUTH0_AUDIENCE");

  return (
    <Auth0Provider
      domain={domain}
      clientId={clientId}
      authorizationParams={{
        redirect_uri: window.location.origin + "/auth/callback",
        audience,
        scope: "openid profile email offline_access",
      }}
      useRefreshTokens={true}
      // When the rotating refresh token is rejected (invalid_grant), retry via a
      // hidden prompt=none iframe against the Auth0 SSO session instead of
      // treating the failure as fatal. Best-effort: the iframe needs third-party
      // cookies, which Safari/Firefox block — Auth0CookieSync's recovery path
      // handles the cases where it can't work.
      useRefreshTokensFallback={true}
      cacheLocation="localstorage"
      onRedirectCallback={onRedirectCallback}
    >
      {children}
    </Auth0Provider>
  );
}
